import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Support = lazy(() => import("./pages/Support"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UserConsole = lazy(() => import("./pages/admin/UserConsole"));
const CategoryManagement = lazy(() => import("./pages/admin/CategoryManagement"));
const CardManagement = lazy(() => import("./pages/admin/CardManagement"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <ThemeProvider 
      attribute="class" 
      defaultTheme="dark" 
      forcedTheme="dark"
      storageKey="theme"
    >
      <BrowserRouter>
        <AdminAuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/support" element={<Support />} />
              
              {/* Admin routes */}
              <Route path="/login/administration" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
              <Route path="/admin/console" element={<ProtectedAdminRoute><UserConsole /></ProtectedAdminRoute>} />
              <Route path="/admin/categories" element={<ProtectedAdminRoute><CategoryManagement /></ProtectedAdminRoute>} />
              <Route path="/admin/cards" element={<ProtectedAdminRoute><CardManagement /></ProtectedAdminRoute>} />
              <Route path="/admin/audit" element={<ProtectedAdminRoute minRole="moderator"><AuditLog /></ProtectedAdminRoute>} />
              <Route path="/admin/settings" element={<ProtectedAdminRoute minRole="superadmin"><AdminSettings /></ProtectedAdminRoute>} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
